import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import {
  createTinganta,
  deleteTinganta,
  getTinganta,
  listTingantas,
  normalizeTingantaScalars,
  updateTinganta,
  TINGANTA_SCALAR_KEYS,
  type Tinganta,
  type TingantaScalars,
} from '../api/tingantaApi';

function buildPatch(prev: Tinganta, next: TingantaScalars): Partial<TingantaScalars> {
  const patch: Partial<TingantaScalars> = {};
  for (const key of TINGANTA_SCALAR_KEYS) {
    if (prev[key] !== next[key]) {
      (patch as Record<string, unknown>)[key] = next[key];
    }
  }
  return patch;
}

function scalarsFromRecord(record: Tinganta): TingantaScalars {
  return normalizeTingantaScalars(record);
}

export function TingantasPage() {
  const [rows, setRows] = useState<Tinganta[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Tinganta | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<Tinganta | null>(null);
  const [form] = Form.useForm<TingantaScalars>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTingantas();
      setRows(data);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load tingantas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(normalizeTingantaScalars({}));
    setModalOpen(true);
  };

  const openEdit = (record: Tinganta) => {
    setEditing(record);
    form.setFieldsValue(scalarsFromRecord(record));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const scalars = normalizeTingantaScalars(values);
      setSaving(true);
      if (editing) {
        const patch = buildPatch(editing, scalars);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateTinganta(editing.id, patch);
        message.success('Tinganta updated');
      } else {
        await createTinganta(scalars);
        message.success('Tinganta created');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setDetail(null);
    try {
      const row = await getTinganta(id);
      setDetail(row);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load tinganta');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteTinganta(id);
      message.success('Tinganta deleted');
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
    }
  };

  const requiredStr = (label: string) => [
    { required: true, message: `${label} is required` },
  ];

  const columns: ColumnsType<Tinganta> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Dhātu', dataIndex: 'dhatu', width: 100, ellipsis: true },
    { title: 'Aupadeśik', dataIndex: 'aupadeshik', width: 120, ellipsis: true },
    { title: 'Gaṇa', dataIndex: 'gana', width: 100, ellipsis: true },
    { title: 'Pada', dataIndex: 'pada', width: 130, ellipsis: true },
    { title: 'Artha', dataIndex: 'artha', ellipsis: true },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => void openDetail(record.id)}
          >
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this tinganta?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => void handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Tinganta"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add tinganta
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        CRUD for <code>/tinganta</code> (verb forms) on the dictionary API (
        {process.env.REACT_APP_DICTIONARY_API_URL || '—'}).
      </Typography.Paragraph>
      <Table<Tinganta>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        scroll={{ x: 960 }}
      />

      <Modal
        title="Tinganta details"
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
        footer={null}
        width={640}
      >
        {detailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : detail ? (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Typography.Text>
              <strong>ID:</strong> {detail.id}
            </Typography.Text>
            {TINGANTA_SCALAR_KEYS.map((key) => (
              <Typography.Paragraph key={key} style={{ marginBottom: 4 }}>
                <Typography.Text strong>{key}:</Typography.Text> {detail[key] || '—'}
              </Typography.Paragraph>
            ))}
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={editing ? 'Edit tinganta' : 'Add tinganta'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void onSubmit()}
        confirmLoading={saving}
        destroyOnClose
        width={720}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Core
          </Typography.Title>
          <Form.Item name="i" label="i" rules={requiredStr('i')}>
            <Input placeholder="e.g. 1" />
          </Form.Item>
          <Form.Item name="baseindex" label="baseindex" rules={requiredStr('baseindex')}>
            <Input placeholder="e.g. 1.1" />
          </Form.Item>
          <Form.Item name="dhatu" label="dhātu" rules={requiredStr('dhātu')}>
            <Input placeholder="e.g. gam" />
          </Form.Item>
          <Form.Item name="aupadeshik" label="aupadeśik" rules={requiredStr('aupadeśik')}>
            <Input placeholder="e.g. गम्" />
          </Form.Item>
          <Form.Item name="gana" label="gaṇa" rules={requiredStr('gaṇa')}>
            <Input placeholder="e.g. bhvadi" />
          </Form.Item>
          <Form.Item name="pada" label="pada" rules={requiredStr('pada')}>
            <Input placeholder="e.g. parasmaipada" />
          </Form.Item>
          <Form.Item name="settva" label="settva" rules={requiredStr('settva')}>
            <Input placeholder="e.g. set" />
          </Form.Item>
          <Form.Item name="karma" label="karma" rules={requiredStr('karma')}>
            <Input placeholder="e.g. sakarmaka" />
          </Form.Item>
          <Form.Item name="artha" label="artha" rules={requiredStr('artha')}>
            <Input placeholder="e.g. to go" />
          </Form.Item>
          <Form.Item
            name="artha_english"
            label="artha_english"
            rules={requiredStr('artha_english')}
          >
            <Input placeholder="e.g. to go" />
          </Form.Item>

          <Typography.Title level={5}>Notes and references</Typography.Title>
          <Form.Item name="tags" label="tags">
            <Input placeholder="e.g. movement,verb" />
          </Form.Item>
          <Form.Item name="dhaturoopnandini" label="dhaturoopnandini">
            <Input placeholder="Reference" />
          </Form.Item>
          <Form.Item name="sk" label="sk">
            <Input />
          </Form.Item>
          <Form.Item name="lsk_sutra" label="lsk_sutra">
            <Input placeholder="Lsk sutra ref" />
          </Form.Item>
          <Form.Item name="sk_sutra" label="sk_sutra">
            <Input placeholder="Sk sutra ref" />
          </Form.Item>
          <Form.Item name="artha_hindi" label="artha_hindi">
            <Input placeholder="e.g. जाना" />
          </Form.Item>
          <Form.Item name="antarganas" label="antarganas">
            <Input placeholder="e.g. sub-gana" />
          </Form.Item>
          <Form.Item name="dhaturoopnandini_note" label="dhaturoopnandini_note">
            <Input.TextArea rows={2} placeholder="Note" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
